import { ReactNode, useContext, useRef, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowsDownUpIcon,
  ArrowUpIcon,
  CalendarPlus,
  ChevronIcon,
  ClockFastForwardIcon,
  ClockRewindIcon,
} from 'assets/icons';
import { AlbumsSortBy } from 'common/enums';
import { DropDownListItem } from 'common/drop-down-list-item';
import { Menu, PositionType } from 'common/menu';
import { AppContext } from 'context/app-context';
import {
  VaultDetailContext,
  vaultDetailActions,
} from 'context/vault-detail-context';
import { classnames } from 'helpers/utils';
import { useClickOutside } from 'hooks/click-outside';
import styles from './sort-dropdown.module.scss';

// Each option carries its own icon, and the trigger shows the active one.
const sortOptions: { value: AlbumsSortBy; label: string; icon: ReactNode }[] = [
  {
    value: AlbumsSortBy.oldestActivity,
    label: 'Oldest first',
    icon: <ClockRewindIcon />,
  },
  {
    value: AlbumsSortBy.recentActivity,
    label: 'Newest first',
    icon: <ClockFastForwardIcon />,
  },
  {
    value: AlbumsSortBy.recentlyAdded,
    label: 'Recently added',
    icon: <CalendarPlus />,
  },
  {
    value: AlbumsSortBy.earliestAdded,
    label: 'Earliest added',
    icon: <CalendarPlus />,
  },
  { value: AlbumsSortBy.aToZ, label: 'A-Z', icon: <ArrowDownIcon /> },
  { value: AlbumsSortBy.zToA, label: 'Z-A', icon: <ArrowUpIcon /> },
];

interface SortDropdownProps {
  // 'small' in a section header (the dashboard's Memories), 'medium' in the
  // vault tabs bar.
  size?: 'small' | 'medium';
}

// The vault's sort (albumsSortBy in the vault-detail context), shared by the
// dashboard, Albums, Memories and Timeline tabs.
export const SortDropdown = ({ size = 'medium' }: SortDropdownProps) => {
  const { state, dispatch } = useContext(VaultDetailContext);
  const [showSort, setShowSort] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  useClickOutside(sortRef, () => setShowSort(false));

  // "Custom order" is offered once the vault's memories have been arranged
  // by hand (in view mode).
  const hasCustomOrder =
    (useContext(AppContext).state.currentVault?.memoryOrder?.length ?? 0) > 0;
  const options = hasCustomOrder
    ? [
        {
          value: AlbumsSortBy.custom,
          label: 'Custom order',
          icon: <ArrowsDownUpIcon />,
        },
        ...sortOptions,
      ]
    : sortOptions;

  const activeSort = options.find(
    (option) => option.value === state.albumsSortBy,
  );
  const sortLabel = activeSort?.label ?? 'Sort';

  return (
    <div
      className={classnames(styles.sortWrap, {
        [styles.small]: size === 'small',
      })}
      ref={sortRef}
    >
      <button
        type="button"
        className={classnames(styles.sortButton, {
          [styles.sortButtonActive]: showSort,
        })}
        onClick={() => setShowSort((prev) => !prev)}
      >
        <span className={styles.sortIcon}>
          {activeSort?.icon ?? <ClockRewindIcon />}
        </span>
        <span className={styles.sortLabel}>{sortLabel}</span>
        <ChevronIcon className={styles.sortChevron} />
      </button>
      {showSort && (
        <Menu position={PositionType.BottomRight} className={styles.sortMenu}>
          {options.map((option) => (
            <DropDownListItem
              key={option.value}
              className={classnames(styles.sortItem, {
                [styles.sortItemActive]: option.value === state.albumsSortBy,
              })}
              leftIcon={option.icon}
              onClick={() => {
                dispatch({
                  type: vaultDetailActions.UPDATE_ALBUMS_SORT_BY,
                  albumsSortBy: option.value,
                });
                setShowSort(false);
              }}
            >
              {option.label}
            </DropDownListItem>
          ))}
        </Menu>
      )}
    </div>
  );
};
